import * as React from "react";
import * as Immutable from "immutable";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { FixedCommentEntityTypeEnum } from "../misc/api/FixedComments/FixedCommentSetEntityTypes";
import { FixedCommentSetTypeEnum } from "../misc/api/FixedComments/FixedCommentSetTypes";
import { FixedComment, Study } from "webportalclient";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";

type FixedCommentsContextProps = {
    currentStudy: Study;
    children: React.ReactNode;
};

interface CommentTypeKey {
    sessionType: SessionTypes;
    setType: FixedCommentSetTypeEnum;
}

interface FixedCommentsContextState {
    session: Immutable.Map<string, Array<FixedComment>>;
    sessionDefaultComments: Immutable.Map<string, string>;
    test: Immutable.Map<string, Array<FixedComment>>;
    testDefaultComments: Immutable.Map<string, string>;
    alerts: Immutable.Map<SessionTypes, Array<FixedComment>>;
}

const initialState: FixedCommentsContextState = {
    session: Immutable.Map<string, Array<FixedComment>>(),
    sessionDefaultComments: Immutable.Map<string, string>(),
    test: Immutable.Map<string, Array<FixedComment>>(),
    testDefaultComments: Immutable.Map<string, string>(),
    alerts: Immutable.Map<SessionTypes, Array<FixedComment>>(),
}

export enum FixedCommentsContextActionTypes {
    LoadSessionFixedComments = "LoadSessionFixedComments",
    LoadTestFixedComments = "LoadTestFixedComments",
    LoadAlertFixedComments = "LoadAlertFixedComments",
}

export class LoadSessionFixedCommentsAction {
    type: FixedCommentsContextActionTypes.LoadSessionFixedComments;
    sessionType: SessionTypes;
    setType: FixedCommentSetTypeEnum;
    defaultComment: string;
    comments: FixedComment[];

    static create(sessionType: SessionTypes, setType: FixedCommentSetTypeEnum, defaultComment: string, comments: FixedComment[]): LoadSessionFixedCommentsAction {
        return {
            type: FixedCommentsContextActionTypes.LoadSessionFixedComments,
            sessionType: sessionType,
            setType: setType,
            defaultComment: defaultComment,
            comments: comments,
        };
    }
}

export class LoadTestFixedCommentsAction {
    type: FixedCommentsContextActionTypes.LoadTestFixedComments;
    sessionType: SessionTypes;
    setType: FixedCommentSetTypeEnum;
    defaultComment: string;
    comments: FixedComment[];

    static create(sessionType: SessionTypes, setType: FixedCommentSetTypeEnum, defaultComment: string, comments: FixedComment[]): LoadTestFixedCommentsAction {
        return {
            type: FixedCommentsContextActionTypes.LoadTestFixedComments,
            sessionType: sessionType,
            setType: setType,
            defaultComment: defaultComment,
            comments: comments,
        };
    }
}

export class LoadAlertFixedCommentsAction {
    type: FixedCommentsContextActionTypes.LoadAlertFixedComments;
    sessionType: SessionTypes;
    defaultComment: string;
    comments: FixedComment[];

    static create(sessionType: SessionTypes, defaultComment: string, comments: FixedComment[]): LoadAlertFixedCommentsAction {
        return {
            type: FixedCommentsContextActionTypes.LoadAlertFixedComments,
            sessionType: sessionType,
            defaultComment: defaultComment,
            comments: comments,
        };
    }
}

type Action = LoadSessionFixedCommentsAction | LoadTestFixedCommentsAction | LoadAlertFixedCommentsAction;

let reducer = (state: FixedCommentsContextState, action: Action) => {
    switch (action.type) {
        case FixedCommentsContextActionTypes.LoadSessionFixedComments:
            return {
                ...state,
                sessionDefaultComments: state.sessionDefaultComments.set(action.sessionType, action.defaultComment),
                session: state.session.set(`${action.sessionType}-${action.setType}`, action.comments),
            };
        case FixedCommentsContextActionTypes.LoadTestFixedComments:
            return {
                ...state,
                testDefaultComments: state.testDefaultComments.set(action.sessionType, action.defaultComment),
                test: state.test.set(`${action.sessionType}-${action.setType}`, action.comments),
            };
        case FixedCommentsContextActionTypes.LoadAlertFixedComments:
            return {
                ...state,
                //testDefaultComments: state.testDefaultComments.set(action.sessionType, action.defaultComment),
                alerts: state.alerts.set(action.sessionType, action.comments),
            };
    }
};

type Dispatch = (action: Action) => void;

const FixedCommentsStateContext = React.createContext<FixedCommentsContextState | undefined>(undefined);
const FixedCommentsDispatchContext = React.createContext<Dispatch | undefined>(undefined);

export const FixedCommentsProvider = ({ currentStudy, children }: FixedCommentsContextProps) => {
    const [fixedCommentState, dispatch] = React.useReducer(reducer, initialState);

    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const fetchFixedComments = async () => {
            if (currentStudy) {
                const commentSets = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${currentStudy.id}`, undefined, `fixedCommentSets($expand=comments)`);

                const sessionSets = commentSets.value.map(ss => ({
                    ...ss,
                    fixedCommentSets: ss.fixedCommentSets.filter(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Session)
                }));

                const testSets = commentSets.value.map(ss => ({
                    ...ss,
                    fixedCommentSets: ss.fixedCommentSets.filter(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Test)
                }));

                const alertSets = commentSets.value.map(ss => ({
                    ...ss,
                    fixedCommentSets: ss.fixedCommentSets.filter(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Alerts)
                }));

                for (const set of sessionSets) {
                    dispatch(LoadSessionFixedCommentsAction.create(set.sessionType as SessionTypes, set.commentSetType as FixedCommentSetTypeEnum, null, set.fixedCommentSets.map(x => x.comments).flat()));
                }

                for (const set of testSets) {
                    dispatch(LoadTestFixedCommentsAction.create(set.sessionType as SessionTypes, set.commentSetType as FixedCommentSetTypeEnum, null, set.fixedCommentSets.map(x => x.comments).flat()));
                }
                for (const set of alertSets.filter(ss => ss.commentSetType === FixedCommentSetTypeEnum.Overread)) {
                    dispatch(LoadAlertFixedCommentsAction.create(set.sessionType as SessionTypes, null, set.fixedCommentSets.map(x => x.comments).flat()));
                }
            }
        }

        fetchFixedComments();
    }, [currentStudy, dispatch])

    return (
        <FixedCommentsStateContext.Provider value={fixedCommentState}>
            <FixedCommentsDispatchContext.Provider value={dispatch}>
                {children}
            </FixedCommentsDispatchContext.Provider>
        </FixedCommentsStateContext.Provider>
    );
}

export const useFixedCommentsState = () => {
    const context = React.useContext(FixedCommentsStateContext);
    if (context === undefined) {
        throw new Error('useFixedCommentsState must be used within a FixedCommentState Provider')
    }

    return context;
}

export const useFixedCommentsDispatch = () => {
    const context = React.useContext(FixedCommentsDispatchContext);
    if (context === undefined) {
        throw new Error('useFixedCommentsDispatch must be used within a FixedCommentDispatch Provider')
    }

    return context;
}
